'use client';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import {
	BigNum,
	InsuranceFundStake,
	SpotMarketAccount,
	getInsuranceFundStakeAccountPublicKey,
	getInsuranceFundVaultPublicKey,
	getSpotMarketPublicKey,
	PublicKey,
} from '@velocity-exchange/sdk';
import {
	MarketId,
	getMultipleAccountsInfoChunked,
} from '@velocity-exchange/common';
import { fetchInsuranceFundData, IfData } from 'src/utils/insuranceFund';
import useVelocityClient from 'src/hooks/useVelocityClient';
import useVelocityClientIsReady from 'src/hooks/useVelocityClientIsReady';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import { useSpotMarketsLookup } from 'src/hooks/spotMarkets/useSpotMarketsLookup';
import { createQueryKey } from 'src/utils/query/createQueryKey';
import useCurrentAuthority from 'src/hooks/useCurrentAuthority';
import useVelocityAccountStore from 'src/stores/useVelocityAccountsStore';
import { AccountLayout } from '@solana/spl-token';
import { useInsuranceFundStatsQuery } from 'src/hooks/query/useInsuranceFundStatsQuery';

type IfDataLookup = Record<number, IfData>;

const REFETCH_INTERVAL_MS = 30_000;
const STALE_TIME_BUFFER_MS = 5_000;
const IF_DATA_BASE_KEY = createQueryKey('ifData');
const EMPTY_IF_DATA: IfDataLookup = {};

export function useAllIfData() {
	const velocityClient = useVelocityClient();
	const velocityClientIsReady = useVelocityClientIsReady();
	const connected = useWalletIsConnected();
	const getOraclePriceForMarket = useGetOraclePriceForMarket();
	const spotMarketsLookup = useSpotMarketsLookup();
	const currentAuthority = useCurrentAuthority();
	const rootAuthority = useVelocityAccountStore(
		(s) => s.getCurrentUserAccount()?.authority ?? currentAuthority
	);
	const { data: insuranceFundStats } = useInsuranceFundStatsQuery();

	const queryFn = useCallback(async (): Promise<IfDataLookup> => {
		const spotMarkets = velocityClient?.getSpotMarketAccounts();
		const connection = velocityClient?.connection;
		const programId = velocityClient?.program.programId;

		if (!spotMarkets || !connection || !programId) return {};

		const lookup: IfDataLookup = {};

		// Build list of accounts to fetch
		const vaultPubkeyPromises = spotMarkets.map((spotMarket) =>
			getInsuranceFundVaultPublicKey(programId, spotMarket.marketIndex)
		);
		const vaultPubkeys = await Promise.all(vaultPubkeyPromises);

		// Fetch the spot market accounts fresh alongside the vault balance and user
		// stake so that `totalShares` (used to derive `currentStake`) is read from
		// the same slot as `ifShares`/`vaultBalance`. Reading `totalShares` from the
		// client's WS subscription cache instead causes a transient phantom earnings
		// right after a stake, until the subscription catches up (or the page is
		// reloaded), because the fresh shares/balance are divided by a stale,
		// too-small `totalShares`.
		const spotMarketPubkeys = await Promise.all(
			spotMarkets.map((spotMarket) =>
				getSpotMarketPublicKey(programId, spotMarket.marketIndex)
			)
		);

		const userStakePubkeys: PublicKey[] = [];
		if (connected && rootAuthority) {
			for (const spotMarket of spotMarkets) {
				const userStakePubkey = getInsuranceFundStakeAccountPublicKey(
					programId,
					rootAuthority,
					spotMarket.marketIndex
				);
				userStakePubkeys.push(userStakePubkey);
			}
		}

		// Fetch all accounts in one RPC call
		const allPubkeys = [
			...vaultPubkeys,
			...spotMarketPubkeys,
			...userStakePubkeys,
		];
		const accountInfos = await getMultipleAccountsInfoChunked(
			connection,
			allPubkeys
		);

		const vaultAccountInfos = accountInfos.slice(0, vaultPubkeys.length);
		const spotMarketAccountInfos = accountInfos.slice(
			vaultPubkeys.length,
			vaultPubkeys.length + spotMarketPubkeys.length
		);
		const userStakeAccountInfos = accountInfos.slice(
			vaultPubkeys.length + spotMarketPubkeys.length
		);

		// Process each market
		for (let i = 0; i < spotMarkets.length; i++) {
			const marketIndex = spotMarkets[i].marketIndex;

			// Prefer the freshly-fetched spot market account so `totalShares` is
			// consistent with the fresh vault balance and user stake; fall back to
			// the cached subscription account if the decode fails.
			let spotMarket: SpotMarketAccount = spotMarkets[i];
			if (spotMarketAccountInfos[i]?.data) {
				try {
					spotMarket = velocityClient.program.coder.accounts.decodeUnchecked(
						'spotMarket',
						spotMarketAccountInfos[i]!.data
					);
				} catch (err) {
					console.warn(
						`[useAllIfData] Failed to decode spot market for market ${marketIndex}, using cached account:`,
						err
					);
				}
			}

			const vaultAccountInfo = vaultAccountInfos[i];

			// Parse vault token balance
			let vaultBalance = BigNum.zero();
			if (vaultAccountInfo?.data) {
				try {
					const tokenAccountData = AccountLayout.decode(vaultAccountInfo.data);
					const balancePrecision = spotMarketsLookup[marketIndex]?.precisionExp;
					if (balancePrecision != null) {
						vaultBalance = BigNum.from(
							tokenAccountData.amount.toString(),
							balancePrecision
						);
					}
				} catch (err) {
					console.warn(
						`[useAllIfData] Failed to parse vault account for market ${marketIndex}:`,
						err
					);
				}
			}

			// Parse user stake account
			let userStakeAccount: InsuranceFundStake | undefined = undefined;
			if (connected && rootAuthority && userStakeAccountInfos[i]?.data) {
				try {
					userStakeAccount = velocityClient.program.coder.accounts.decode(
						'insuranceFundStake',
						userStakeAccountInfos[i]!.data
					);
				} catch (err) {
					console.warn(
						`[useAllIfData] Failed to decode user stake account for market ${marketIndex}:`,
						err
					);
				}
			}

			const oraclePrice =
				getOraclePriceForMarket(
					MarketId.createSpotMarket(marketIndex)
				)?.toNum() || 0;

			// Get 7-day APY from insurance fund stats
			let sevenDayApy = 0;
			if (insuranceFundStats?.marketSharePriceData) {
				const marketData = insuranceFundStats.marketSharePriceData.find(
					(data) => data.marketIndex === marketIndex
				);
				if (marketData?.apy !== undefined) {
					const apy = Number(marketData.apy);
					sevenDayApy = !isNaN(apy) && apy >= 0.01 ? apy : 0;
				}
			}

			const ifData = fetchInsuranceFundData(
				spotMarket,
				oraclePrice,
				vaultBalance,
				userStakeAccount,
				connected,
				sevenDayApy
			);

			lookup[marketIndex] = ifData;
		}

		return lookup;
	}, [
		velocityClient,
		connected,
		rootAuthority,
		getOraclePriceForMarket,
		spotMarketsLookup,
		insuranceFundStats,
	]);

	const queryClient = useQueryClient();

	// Refetch when auth state changes or insuranceFundStats loads,
	// instead of changing query key, so existing cached data stays visible during the refetch.
	useEffect(() => {
		queryClient.invalidateQueries({ queryKey: IF_DATA_BASE_KEY });
	}, [connected, rootAuthority, insuranceFundStats, queryClient]);

	return useQuery({
		queryKey: IF_DATA_BASE_KEY,
		queryFn,
		enabled: velocityClientIsReady,
		refetchInterval: REFETCH_INTERVAL_MS,
		staleTime: REFETCH_INTERVAL_MS - STALE_TIME_BUFFER_MS,
		initialData: EMPTY_IF_DATA,
	});
}

/**
 * Hook to invalidate IF data without subscribing to it.
 * Use this when you need to trigger a refetch (e.g., after stake/unstake)
 * but don't want to subscribe to the data and cause unnecessary re-renders.
 */
export function useTriggerRefreshIfStakeData() {
	const queryClient = useQueryClient();

	const refresh = useCallback(async () => {
		await queryClient.invalidateQueries({
			queryKey: IF_DATA_BASE_KEY,
		});
	}, [queryClient]);

	return refresh;
}
